import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { AREAS, CHAIN, HERO_PLASMA, SOUL_DNA_CAPTION } from '../../lib/u99';
import { Reveal, Eyebrow } from '../../components/Reveal';
import { LabelLegend } from '../../components/u99/Label';
import { Chip } from '../../components/u99/PageHead';
import EternalJourney from '../../components/u99/EternalJourney';
import EliElyFeature from '../../components/u99/EliElyFeature';
import BookFeature from '../../components/u99/BookFeature';

const AreaCard = ({ a, i }) => {
  const Icon = a.icon;
  return (
    <Reveal delay={i * 0.07}>
      <Link to={a.to} className="group block h-full rounded-2xl border border-white/[0.07] hover:border-amber-300/40 p-6 lg:p-8 transition-colors duration-500 relative overflow-hidden" data-testid={`u99-area-card-${a.id}`}>
        <span className="absolute -right-4 -top-6 font-celestial text-[7rem] leading-none text-white/[0.025] select-none">{String(i + 1).padStart(2, '0')}</span>
        <Icon size={22} strokeWidth={1.5} className="text-amber-300" />
        <h3 className="mt-6 font-celestial text-xl text-slate-100 group-hover:text-amber-100 transition-colors">{a.label}</h3>
        <p className="mt-3 text-sm text-slate-400 leading-relaxed">{a.blurb}</p>
        <span className="mt-6 inline-flex items-center gap-2 font-mono-precision text-[10px] uppercase tracking-[0.25em] text-slate-500 group-hover:text-amber-300 transition-colors">Enter <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" /></span>
      </Link>
    </Reveal>
  );
};

export default function U99Home() {
  return (
    <main className="max-w-7xl mx-auto px-5 lg:px-10" data-testid="u99-home">
      <section className="relative pt-32 lg:pt-44 pb-16 lg:pb-24">
        <div className="absolute left-1/2 -translate-x-1/2 w-screen -top-16 bottom-0 pointer-events-none overflow-hidden" data-testid="u99-home-hero-anchor">
          <motion.img initial={{ opacity: 0, scale: 1.1 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 2.4, ease: [0.22, 1, 0.36, 1] }} src={HERO_PLASMA} alt={SOUL_DNA_CAPTION} className="absolute right-0 top-0 h-full w-full lg:w-[58%] object-cover opacity-60" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#02040a] via-[#02040a]/85 to-[#02040a]/10" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#02040a]/30 via-transparent to-[#02040a]" />
        </div>
        <div className="relative">
        <Chip testId="u99-live-chip">cosmosres.com · Live</Chip>
        <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }} className="mt-8 font-celestial text-5xl sm:text-6xl lg:text-8xl font-light leading-[0.95] text-slate-100">
          United <span className="text-amber-300">99</span><br />Unleashed
        </motion.h1>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.9, delay: 0.3 }} className="mt-8 text-base md:text-lg text-slate-400 leading-relaxed max-w-2xl">
          Placeholder tagline. One mind, five rooms: the doctrines Kyle Hinton is writing, the tools that grow out of them, and the documents behind both — presented as readable pages, labelled for exactly what they are.
        </motion.p>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="mt-12 flex flex-wrap items-center gap-1 sm:gap-2" data-testid="u99-chain-strip">
          {CHAIN.map((c, i) => (
            <span key={c} className="flex items-center gap-1 sm:gap-2">
              {i > 0 && <span className="text-amber-400/60 font-mono-precision text-xs">→</span>}
              <span className="font-mono-precision text-[10px] sm:text-[11px] uppercase tracking-[0.2em] text-slate-300">{c}</span>
            </span>
          ))}
        </motion.div>
        </div>
      </section>

      <EternalJourney />

      <section className="py-12 lg:py-20" data-testid="u99-home-books">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <Eyebrow accent="#E879F9">The books · free to read, no sign-up</Eyebrow>
            <Link to="/u99/library" className="font-mono-precision text-[10px] uppercase tracking-[0.25em] text-amber-300 hover:text-amber-200 transition-colors" data-testid="u99-home-library-link">Full library index →</Link>
          </div>
        </Reveal>
        <div className="grid lg:grid-cols-2 gap-5">
          <EliElyFeature />
          <BookFeature compact />
        </div>
      </section>

      <section className="py-12 lg:py-20">
        <Reveal><Eyebrow>Five areas</Eyebrow></Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 lg:gap-5">
          {AREAS.map((a, i) => <AreaCard key={a.id} a={a} i={i} />)}
        </div>
      </section>

      <section className="py-12 lg:py-20 grid lg:grid-cols-12 gap-10">
        <Reveal className="lg:col-span-5">
          <Eyebrow accent="#22D3EE">The connecting identity</Eyebrow>
          <h2 className="font-celestial text-3xl sm:text-4xl text-slate-100 leading-tight">Everything here passes through <span className="text-cyan-300">Kyle's Mind</span></h2>
          <p className="mt-6 text-sm sm:text-base text-slate-400 leading-relaxed">Placeholder. The doctrines are not a system handed down; they are one person's attempt to trace how experience becomes character. Kyle's Mind is the room where the threads are visibly held together.</p>
          <Link to="/u99/mind" className="mt-8 inline-flex items-center gap-3 font-mono-precision text-[11px] uppercase tracking-[0.25em] text-slate-300 hover:text-cyan-300 transition-colors" data-testid="u99-home-mind-link">Enter Kyle's Mind <ArrowRight size={14} /></Link>
        </Reveal>
        <Reveal className="lg:col-span-7" delay={0.1}>
          <Eyebrow>How claims are labelled</Eyebrow>
          <LabelLegend />
        </Reveal>
      </section>
    </main>
  );
}
